import { Box, Button, ButtonGroup, Input, Textarea, useDisclosure } from '@chakra-ui/react';
import { ReactElement, useId, useState } from 'react';
import { CustomFormControl, Modal } from 'shared-components';

interface EditTextFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  title: string;
  fieldLabel: string;
  fieldSubLabel?: string;
  placeholder?: string;
  initialValue?: string;
  required?: boolean;
  rows?: number;
  singleLine?: boolean;
  confirmLabel?: string;
}

export const EditTextFieldModal = ({
  isOpen,
  onClose,
  onSave,
  title,
  fieldLabel,
  fieldSubLabel,
  placeholder = 'Add text...',
  initialValue = '',
  required = false,
  rows = 3,
  singleLine = false,
  confirmLabel = 'Save changes',
}: EditTextFieldModalProps): ReactElement => {
  const [value, setValue] = useState<string | null>(null);
  const fieldId = useId();

  const { onClose: handleClose } = useDisclosure({
    isOpen,
    onClose: () => {
      setValue(null);
      onClose();
    },
  });

  const currentValue = value ?? initialValue;
  const isSaveDisabled = required && (!currentValue || currentValue.trim() === '');

  const handleSave = (): void => {
    onSave(currentValue);
    handleClose();
  };

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title={title}
      footer={
        <ButtonGroup spacing={3} size="lg">
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="solid" colorScheme="blue" onClick={handleSave} isDisabled={isSaveDisabled}>
            {confirmLabel}
          </Button>
        </ButtonGroup>
      }
    >
      <Box>
        <CustomFormControl
          id={fieldId}
          label={fieldLabel}
          labelProps={{ htmlFor: fieldId }}
          subLabel={fieldSubLabel}
          size="lg"
        >
          {singleLine ? (
            <Input
              id={fieldId}
              placeholder={placeholder}
              value={currentValue}
              onChange={e => setValue(e.target.value)}
            />
          ) : (
            <Textarea
              id={fieldId}
              placeholder={placeholder}
              rows={rows}
              value={currentValue}
              onChange={e => setValue(e.target.value)}
            />
          )}
        </CustomFormControl>
      </Box>
    </Modal>
  );
};
